import { createRoute, useParams, useSearch } from '@tanstack/react-router';
import { msg, T } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { orpc } from '@/api/rpc/client';
import { Spinner } from '@/components/ui/spinner';
import {
  Route as pathlessAuthenticatedRoute,
  requirePermission,
} from '../../route';
import { useTaskPage } from 'src/api/rpc/hooks/tasks/useTaskPages';
import { tasksQueueSearchSchema } from '../../tasks/taskQueueSearch';
import { TaskPageQueue } from './TaskPageQueue';
import {
  hasTaskPageTabs,
  resolveActiveTab,
  taskPageTabSearchSchema,
  taskPageTabs,
} from './taskPageTabs';

const TASK_PAGE_CRUMB_FALLBACK = msg('Page', {
  context: 'breadcrumb fallback when a task page name cannot be loaded',
});

/**
 * `?type=` is meaningless here — the page pins the task type. `?tab=` picks
 * the active task type on a page that shows several.
 */
const taskPageQueueSearchSchema = tasksQueueSearchSchema
  .omit({ type: true })
  .extend(taskPageTabSearchSchema.shape);

function TaskPageRoutePage() {
  const { taskPageId } = useParams({
    from: '/authenticated/pages/$taskPageId',
  });
  const { tab: tabParam } = useSearch({
    from: '/authenticated/pages/$taskPageId',
  });
  const { data: page, isLoading, error } = useTaskPage(taskPageId);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <T context="empty state when a task page does not exist or is not visible to the user">
          This page does not exist or you do not have access to it.
        </T>
      </div>
    );
  }

  const tabs = taskPageTabs(page);
  const activeTab = resolveActiveTab(tabs, tabParam);
  // Key by page + tab so navigating between task pages or tabs remounts the
  // queue (resetting the one-shot default-filter seeding, the search input
  // and the column config, which is per tab).
  return (
    <TaskPageQueue
      key={`${page.id}:${activeTab.key}`}
      page={page}
      tab={activeTab}
      tabs={hasTaskPageTabs(tabs) ? tabs : null}
    />
  );
}

/**
 * Generic org-configured task page (`core_task_pages`) — the tasks queue
 * pinned to the page's task type (one tab per type when it shows several),
 * mounted in the sidebar by the taskPages
 * sidebar merge. Gated by Read on TaskPage (plus the page's user-group
 * audience, enforced server-side: `taskPages.get` returns NOT_FOUND for
 * non-visible pages).
 */
export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/pages/$taskPageId',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.TaskPage }],
      },
    },
  },
  component: TaskPageRoutePage,
  validateSearch: taskPageQueueSearchSchema,
  loader: async ({ params, context }) => {
    // Fetch the page name for the breadcrumb, cached via queryClient.
    try {
      const page = await context.queryClient.fetchQuery(
        orpc.taskPages.get.queryOptions({
          input: { id: params.taskPageId },
        }),
      );
      return { crumb: page.name };
    } catch {
      // Left encoded: the breadcrumb bar decodes it in the active locale.
      return { crumb: TASK_PAGE_CRUMB_FALLBACK };
    }
  },
  beforeLoad: async ({ context }) => {
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.TaskPage,
    });
  },
});
